import type {
  VersionResolutionWarning,
  VersionString,
  ViewManifest,
} from "../manifest/types";
import type { ModuleID, ModuleInstanceKey, ViewID } from "../types/ids";
import type { FlowMeta } from "../flow/types";
import type { View, WireErrorViewRef, WireModuleRef } from "../view/types";

// ============================================================================
// BundledView
// ============================================================================

/**
 * A view whose bundle has been loaded from CDN and whose routing metadata has
 * been computed (step 3 of the pipeline).
 *
 * Produced by `loadViewBundles` for each `ViewRef` in the matched branch. The
 * view definition is a complete `View` object — the computed fields
 * (`qualifiedRoute`, `qualifiedId`, `routeSpecificity`) have been applied.
 * The view-level manifest is intentionally absent at this stage; it is fetched
 * for only the matched view in step 4 (path matching), yielding a
 * {@link LoadedView}.
 *
 * **Server-internal only.** `BundledView` holds a live `View` object with
 * functions (`dataFetcher`, `routeGuard`, `modules`, …) that cannot survive
 * JSON serialization. It must not cross the wire — use
 * {@link LoadedViewResponse} in response payloads.
 */
export interface BundledView {
  /** Full view definition with computed qualifiedRoute, qualifiedId, routeSpecificity */
  definition: View;
  /** Exact semver version of the bundle that was loaded */
  viewVersion: VersionString;
}

// ============================================================================
// LoadedView
// ============================================================================

/**
 * A {@link BundledView} promoted with its view-level manifest.
 *
 * Produced after step 4 (path matching) resolves the winning view and its
 * manifest is fetched. The manifest pins the exact module versions the browser
 * should load when rendering this view.
 *
 * **Server-internal only.** Inherits the non-serializable `definition: View`
 * from {@link BundledView}. The wire counterpart is {@link LoadedViewResponse}.
 */
export interface LoadedView extends BundledView {
  /** View-level manifest — pins module versions for this view version */
  manifest: ViewManifest;
}

// ============================================================================
// LoadedViewResponse (wire shape)
// ============================================================================

/**
 * The wire-safe view identity shipped inside a {@link ViewResponse}.
 *
 * Carries only the serializable fields the client needs to request the right
 * bundle from the CDN and reason about pinned module versions. The full
 * `View` definition is intentionally omitted — it contains functions that do
 * not survive JSON serialization, and the client imports the view module
 * fresh rather than reconstructing it from the response.
 */
export interface LoadedViewResponse {
  /** Unique ID of the matched view (e.g. `"welcome"`) */
  viewId: ViewID;
  /** Exact semver version of the bundle the server loaded */
  viewVersion: VersionString;
  /** View-level manifest — pins module versions for this view version */
  manifest: ViewManifest;
}

// ============================================================================
// Data Fetcher Warning
// ============================================================================

/**
 * A warning emitted when the view-level `dataFetcher` throws but the pipeline
 * continues with `undefined` viewData rather than failing hard.
 *
 * Module-level `dataFetcher` failures are fatal and bubble up as
 * `module_load_failed` — they do not produce a warning.
 *
 * The `kind: "dataFetcher"` discriminant distinguishes this from
 * {@link ViewVersionWarning} and {@link ModuleVersionWarning} in the shared
 * `warnings` array on {@link ViewResponse}.
 */
export interface DataFetcherWarning {
  /** Discriminant — identifies this as a data-fetcher failure warning */
  kind: "dataFetcher";
  /**
   * The origin of the failure — always `"view"` (the view-level `dataFetcher` threw).
   */
  source: "view";
  /** `err.message` when the thrown value is an `Error`; `String(err)` otherwise */
  error: string;
}

// ============================================================================
// View Version Warning
// ============================================================================

/**
 * A version resolution warning emitted for a single **view** during view loading.
 *
 * The `kind: "view"` discriminant distinguishes this from {@link ModuleVersionWarning}
 * in the shared `warnings` array on {@link ViewResponse}.
 *
 * Present when the branch manifest was absent, did not contain an entry for
 * the view, or its pinned version was incompatible with the view's declared
 * `minVersion`. In all cases, `resolvedVersion` is `ViewRef.minVersion`.
 */
export interface ViewVersionWarning {
  /** Discriminant — identifies this as a view-level version warning */
  kind: "view";
  /** ID of the view that triggered the warning */
  viewId: ViewID;
  /** Reason the pinned manifest version was not used */
  warning: VersionResolutionWarning;
  /** The version that was ultimately loaded (always `ViewRef.minVersion`) */
  resolvedVersion: VersionString;
}

// ============================================================================
// Module Version Warning
// ============================================================================

/**
 * A version resolution warning emitted for a single **module** during data fetching.
 *
 * The `kind: "module"` discriminant distinguishes this from {@link ViewVersionWarning}
 * in the shared `warnings` array on {@link ViewResponse}.
 *
 * Present when the view manifest was absent for a module, its pinned version
 * had a different major, or the manifest was behind the module ref's
 * `minVersion`. In all cases, `resolvedVersion` is the module ref's `minVersion`.
 */
export interface ModuleVersionWarning {
  /** Discriminant — identifies this as a module-level version warning */
  kind: "module";
  /** ID of the module that triggered the warning */
  moduleId: ModuleID;
  /** Reason the pinned manifest version was not used */
  warning: VersionResolutionWarning;
  /** The version that was ultimately loaded (always the module ref's `minVersion`) */
  resolvedVersion: VersionString;
}

/**
 * Discriminated union of all warning variants that may appear in the
 * `warnings` array on {@link ViewResponse}.
 */
export type ResponseWarning =
  | ViewVersionWarning
  | ModuleVersionWarning
  | DataFetcherWarning;

// ============================================================================
// Resolved Navigation Targets
// ============================================================================

/**
 * A navigation interaction whose target has been resolved server-side,
 * keyed by interaction name in {@link ViewResponse.resolvedInteractions}.
 *
 * The server resolves the *target* (viewId / branchId → route template)
 * but does NOT compile the URL — that happens on the client at click
 * time, where path-param values from the current request are merged with
 * any caller-supplied params. This split keeps the server's job to the
 * one thing it can uniquely do (knowing the registered flow / branch /
 * view set) and lets the client own URL formatting without a sentinel
 * dance for caller-supplied values.
 *
 * The discriminant `kind` mirrors the three forms of
 * {@link NavigationInteraction}:
 * - `"view"`     — internal navigation. `route` is the target view's
 *                  fully-qualified route template (still containing any
 *                  `:name` tokens). `query` is the interaction's static
 *                  query map verbatim — values of the form `":name"` are
 *                  treated as path-param references and resolved by the
 *                  client alongside the path. `persistSearch` is carried
 *                  through so the client can decide whether to merge
 *                  `window.location.search` at navigation time.
 * - `"external"` — the interaction's `externalUrl`, passed through verbatim,
 *                  or already prefixed with the matched flow's `basePath`
 *                  when the interaction set `relativeToFlow`. Either way the
 *                  client navigates to `url` as-is.
 * - `"back"`     — passthrough of the `browserBack: true` form.
 */
export type ResolvedNavigationTarget =
  | {
      kind: "view";
      route: string;
      query?: Record<string, string>;
      persistSearch?: boolean;
    }
  | { kind: "external"; url: string }
  | { kind: "back" };

// ============================================================================
// Loader Responses
// ============================================================================

/**
 * Server-fetched module data keyed by {@link ModuleInstanceKey}, so two
 * instances of one module in a view keep their own data. During the FEP2-4678
 * rollout servers also emit each module's data under its bare module id for
 * shells that predate instance keys; read entries with `readModuleData`.
 */
export type ModuleDataMap = Partial<Record<ModuleInstanceKey | ModuleID, unknown>>;

/**
 * Terminal success response — the winning view and its manifest are ready to render
 * and server-side data has been fetched. Module bundles are loaded by the
 * client from the CDN.
 *
 * Returned by `handleLoader` when the full pipeline (steps 1–7) completes
 * without triggering a redirect.
 *
 * `warnings` is a flat array of view-level, module-level, and data-fetcher
 * failure warnings. Each entry is discriminated by `kind`.
 */
export interface ViewResponse {
  type: "view";
  /**
   * Wire-safe identity of the matched view — `viewId`, `viewVersion`, and the
   * view-level manifest. The client uses these to import the view module from
   * the CDN; the server's live `View` object is not included in the response
   * because its functions cannot be serialized.
   */
  loadedView: LoadedViewResponse;
  /** Server-fetched data returned by the view's dataFetcher (null if no dataFetcher, it returned nothing, or it threw) */
  viewData: unknown;
  /** Per-module server-fetched data, keyed by module instance */
  moduleDataMap: ModuleDataMap;
  /**
   * The resolved layout module reference for this view: the view's
   * `layoutOverride` if present, otherwise the flow's layout. Carried as a
   * {@link WireModuleRef} (no `props`) because `layoutOverride` may declare
   * function-form props that can't survive JSON serialisation; the client
   * re-evaluates `layoutOverride` from the imported view bundle when it
   * needs the concrete props.
   */
  layoutModuleRef: WireModuleRef;
  /**
   * Static props of the resolved layout when they are a plain record (the
   * flow's `layout`, or a `layoutOverride` with record props). Absent when the
   * override declares callback props; the client evaluates those from the
   * view bundle.
   */
  layoutProps?: Record<string, unknown>;
  /**
   * Resolved module versions actually loaded by the server.
   * Keyed by module ID, value is the exact version loaded (after manifest
   * version resolution). Together with `loadedView.viewVersion`, forms a
   * complete "resolved manifest" showing what the server loaded.
   */
  resolvedModuleVersions: Record<ModuleID, VersionString>;
  /**
   * Flat array of version resolution warnings and data-fetcher failure warnings.
   * Discriminated by `kind: "view" | "module" | "dataFetcher"`.
   */
  warnings: Array<ResponseWarning>;
  /**
   * Pre-resolved targets for the matched view's navigation interactions,
   * keyed by interaction name. Refresh and action interactions never appear
   * here — only navigation interactions that the server could resolve. A
   * navigation interaction whose `viewId` (or `branchId`) could not be
   * resolved is omitted entirely; the client surfaces a clear error if such
   * a handler is ever invoked.
   */
  resolvedInteractions: Record<string, ResolvedNavigationTarget>;
  /**
   * The current request's path params, as matched against the winning
   * view's `qualifiedRoute`. The client merges this with caller-supplied
   * params at click time to compile navigation URLs; threading them on
   * the loader response avoids the client having to re-implement route
   * matching against a `path: "*"` router.
   */
  pathParams: Record<string, string>;
  /**
   * The matched flow's document-head metadata (e.g. the base page title),
   * copied from the required `flow.meta`. The flow
   * is server-only, so this is the sole channel by which its base title reaches
   * the client; the client composes it with the view's own title for the
   * document head. The view's own `meta` is read client-side from the view
   * bundle and is deliberately not carried here.
   */
  flowMeta: FlowMeta;
}

/**
 * Terminal redirect response — the pipeline resolved to a different URL.
 *
 * Returned when the route guard returns `false`, or the matched view is a
 * resolver view (`isResolver: true`). The resolution hierarchy (branchResolver
 * → viewResolver) determined the target location.
 */
export interface RedirectResponse {
  type: "redirect";
  /** Fully-qualified URL path to redirect the client to */
  location: string;
  /**
   * When `true`, the target lives outside the Tinkertoy shell (e.g. a route
   * still served by the legacy v1 host during a migration) and the client must
   * perform a full-document navigation (`window.location.assign`) rather than a
   * client-side React Router redirect. Omitted/`false` for in-shell redirects.
   */
  external?: boolean;
}

/**
 * Error response — something went wrong that prevented request handling.
 *
 * Always returned with the corresponding HTTP status code.
 *
 * When a flow was resolved before the error occurred, `errorView` and
 * `flowMeta` are present so the client can render the flow's configured error
 * view and preserve the carrier-appropriate document title. `errorContext`
 * provides machine-readable detail about what failed.
 *
 * Pre-flow errors (`no_flow_match`, flow matcher crash) have none of these fields.
 */
export interface LoaderErrorResponse {
  type: "error";
  /** HTTP status code (e.g. 404, 500) */
  status: number;
  /** Human-readable error message */
  message: string;
  /** Machine-readable error code for programmatic handling */
  code: LoaderErrorCode;
  /**
   * The flow's configured error-view module reference.
   * Present only when a flow was resolved before the error occurred.
   */
  errorView?: WireErrorViewRef;
  /**
   * Machine-readable context about the failure.
   * Present only when a flow was resolved before the error occurred.
   */
  errorContext?: { errorType: string; moduleId?: string };
  /**
   * The matched flow's document-head metadata (e.g. the base page title),
   * copied from the required `flow.meta`. Present under the same conditions
   * as `errorView` so the shell's error boundary can preserve the carrier
   * title when `TinkertoyShell` unmounts.
   */
  flowMeta?: FlowMeta;
  /**
   * Result of invoking the `errorView` module's `dataFetcher` (if it declares
   * one) with the request's `ServerContext`. Undefined when no flow was
   * resolved, the module declares no `dataFetcher`, or the fetch failed —
   * the error page must always be renderable without it.
   */
  errorModuleData?: unknown;
}

/**
 * Discriminated union of all possible loader responses.
 */
export type LoaderResponse =
  | ViewResponse
  | RedirectResponse
  | LoaderErrorResponse;

/**
 * Error codes for loader error responses.
 */
export type LoaderErrorCode =
  | "no_flow_match" // step 1: no flow matched the request
  | "branch_not_found" // step 2: branchResolver returned an unknown branch ID
  | "views_load_failed" // step 3: view bundle or manifest loading threw
  | "no_view_match" // step 4: no view's qualifiedRoute matched the request path
  | "ambiguous_route" // step 4–5: two views share identical route specificity
  | "module_load_failed" // step 6: a module bundle failed to load during data fetching
  | "auth_failed" // step 7: authentication check failed or misconfigured
  | "view_not_found" // steps 9–10: resolution hierarchy produced an unknown view ID
  | "redirect_loop" // steps 9–10: hop count exceeded the configured maximum
  | "add_response_headers_failed" // addResponseHeaders threw
  | "internal_error"; // unexpected failure in flow or branch matching

// ============================================================================
// Progressive loader stream (opt-in NDJSON)
// ============================================================================

/** Wire media type negotiated via `Accept` for assets-then-terminal streaming. */
export const LOADER_NDJSON_MEDIA_TYPE = "application/x-ndjson";

/** Protocol version carried on each NDJSON chunk. Bump only with a breaking wire change. */
export const LOADER_STREAM_PROTOCOL_VERSION = 1;

/**
 * Loading module the shell renders while a view loads: the view's
 * `loadingOverride` if set, otherwise the flow's `loading`. `version` is the
 * server-resolved version (manifest pin for an override, `minVersion` for the
 * flow default), unlike {@link WireModuleRef}'s `minVersion`.
 *
 * Never listed in `resolvedModuleVersions` or `moduleRefs`: clients prime every
 * entry there through a module loader that rejects the `"loading"` type.
 */
export interface WireLoadingModule {
  moduleId: ModuleID;
  version: VersionString;
  props: Record<string, unknown>;
}

/**
 * The resolved layout plus its server-fetched data, streamed ahead of the
 * terminal chunk so the shell can render the loading module inside the layout
 * on first load. Only sent when the route guard passed and the view is not a
 * resolver view.
 *
 * `data` is absent on the wire when the layout has no `dataFetcher`.
 */
export type WireEarlyLayout = WireLoadingModule & { data: unknown };

/**
 * Static asset metadata the client needs to begin CDN imports before module
 * data fetchers complete. Provisional only — a later terminal chunk may
 * redirect or error.
 *
 * A stream may carry more than one assets chunk; each repeats the base fields.
 * The loading fields are present only when the flow or view configures a
 * loading module, and are ignored without `loadingModule`. A later chunk merges
 * into the earlier one: a loading field it omits keeps its previous value.
 *
 * The layout's data is fetched once — streamed in {@link layout} and repeated
 * in the terminal response's `moduleDataMap`. Its version is always
 * `resolvedModuleVersions[layoutModuleRef.moduleId]`.
 */
export interface LoaderAssetsPayload {
  loadedView: LoadedViewResponse;
  layoutModuleRef: WireModuleRef;
  resolvedModuleVersions: Record<ModuleID, VersionString>;
  /** Wire-safe module refs for the static module tree (layout + content). */
  moduleRefs: Array<WireModuleRef>;
  /** Loading module to render while this view loads. */
  loadingModule?: WireLoadingModule;
  /**
   * Whether a later assets chunk may carry {@link layout}. `false` once the
   * server knows none will (guard failed, resolver view, or callback layout
   * props), so the shell renders the loading module standalone.
   */
  earlyLayout?: boolean;
  /**
   * Layout and its data, sent in a follow-up assets chunk. Takes precedence
   * over `earlyLayout: false`.
   */
  layout?: WireEarlyLayout;
  /** The matched flow's document-head metadata, for the first-load title. */
  flowMeta?: FlowMeta;
}

/** First chunk on a negotiated streaming `/load` response. */
export interface LoaderAssetsChunk {
  type: "assets";
  protocolVersion: typeof LOADER_STREAM_PROTOCOL_VERSION;
  payload: LoaderAssetsPayload;
}

/**
 * Final chunk on a streaming `/load` response. Wraps the same {@link LoaderResponse}
 * shapes legacy clients receive as a single JSON body.
 */
export interface LoaderTerminalChunk {
  type: "terminal";
  protocolVersion: typeof LOADER_STREAM_PROTOCOL_VERSION;
  /** HTTP status the legacy JSON path would have returned for this response. */
  status: number;
  response: LoaderResponse;
}

/** One NDJSON record on a negotiated streaming `/load` response. */
export type LoaderStreamChunk = LoaderAssetsChunk | LoaderTerminalChunk;
